import { bizmatesSiteHomeUrl, errorCodes, lessonResultCode } from '@/utils/constants';

export default {
  messages: {
    leaveMyStageConfirmation: {
      dialogTitle: 'Leave the MyStage lesson room?',
      beforeLessonStart: 'Your lesson is scheduled to start at {0}.',
      duringLessonTime: 'The room will remain accessible until your lesson ends at <br/>{0}.',
      duringGracePeriod: 'You will NOT be able to reconnect with your student if you leave now.',
      afterGracePeriod: 'You will be able to return to this page at any time until you register the PEDA for this lesson.',
      controls: {
        leave: 'Leave',
        confirm: 'Leave',
        cancel: 'Return to lesson',
      },
      testCallLeave: 'To leave the Test Call, please close the tab.'
    },
    lessonTimer: {
      beforeLessonStart: 'Lesson start time',
      lessonHasEnded: 'The lesson has ended.',
      timerLabel: 'Remaining time',
      nextLessonMessage: 'Your next class starts in 2 minutes. Please wrap up your lesson, properly exit the MyStage lesson room, and be on time for your next class.'
    },
    videoMessage: {
      waitingForLesson: 'Waiting for lesson to start.',
      connectionProblem: 'We encountered a problem while trying to connect. Please refresh the page.',
      waitingForOtherParty: 'Please wait for your student to join the room. ',
      simultaneousConnection: 'You have connected to MyStage from another device.',
      lessonEnded: 'The lesson has ended.',
      streamError: {
        unexpectedError: '<h2>An unexpected error occured.</h2> Please refresh the page.',
        deviceInvalid: '<h2>An unexpected error occured.</h2> Please refresh the page.',
      },
      noInternetConnection: 'No Internet. Please check your connection.',
      onlineStatus: 'The student has entered the lesson room. Please wait till the lesson starts.'
    },
    deviceErrorGuide: {
      tooltip: 'Show more info',
      camera: 'camera',
      mic: 'mic',
      title: {
        notFound: 'Unable to use your {device}',
        permissionDenied: 'Permission needed for your {device}'
      },
      permissionDenied: {
        chrome:
          '<ol><li>Click the settings icon in your browser\'s address bar</li>' +
          '<li>Allow/Enable the {device} device</li>' +
          '<li>Reload the MyStage lesson page</li></ol>',
        edge:
          '<ol><li>Click the settings icon in your browser\'s address bar</li>' +
          '<li>Allow/Enable the {device} device</li>' +
          '<li>Reload the MyStage lesson page</li></ol>',
        firefox:
          '<ol><li>Click the settings icon in your browser\'s address bar</li>' +
          '<li>Clear the blocked state of {device}</ol>',
        safari:
          '<ul><li>Reload the MyStage page and allow {device}' +
          '<li>Go to <b>Safari > Settings > Websites</b>, ensure the <b>MyStage</b> site has access to {device}</ul>',
        chromeMacOS:
          '<ol><li>Click the settings icon in your browser\'s address bar</li>' +
          '<li>Allow/Enable the {device} device</li>' +
          '<li>Reload the MyStage lesson page</li>' +
          '<li>Ensure that camera access is enabled in <b>Apple menu > System Settings</b></li></ol>'
      },
      notFound:
        '<ul><li>Close other applications that might be using your {device}</li>' +
        '<li>Check to make sure it\'s connected and installed properly</li>' +
        '<li>Restart your browser</li>' +
        '<li>If the issue persists, try a different browser to rule out browser-specific problems</li></ul>',
      notFoundMacOS:
        '<ul><li>Close other applications that might be using your {device}</li>' +
        '<li>Check to make sure it\'s connected and installed properly</li>' +
        '<li>Restart your browser</li>' +
        '<li>If the issue persists, try a different browser to rule out browser-specific problems</li>' +
        '<li>Ensure that camera access is enabled in <b>Apple menu > System Settings</b></li></ul>'
    },
    chatBox: {
      deleteLabel: 'Delete',
      deletedMessage: 'Message was deleted',
      editLabel: 'Edit'
    },
    usefulPhrases: {
      buttonLabel: 'Input'
    },
    buttons: {
      share: 'Share',
      reload: 'Reload',
      record: 'Record',
      recording: 'Recording',
      'record-stop': 'Stop Recording'
    },
    discoveryMaterial: {
      level: 'レベル'
    },
    htmlMaterial: {
      title: '{0}',
      level: 'Level',
      print: 'Print',
      download: 'Download PDF',
      downloadHtml: 'Download HTML'
    },
    materialControls: {
      previous: 'Previous',
      next: 'Next',
      change: 'Change Material'
    },
    lessonResult: {
      unknown: 'There was a problem during this lesson',
      [lessonResultCode.TRAINER_REQUESTS_SKYPE]: 'This lesson was terminated because you reported a problem with MyStage .',
      [lessonResultCode.STUDENT_NOSHOW]: 'This lesson did not take place due to a student no-show.',
      [lessonResultCode.STUDENT_ABSENT]: 'This lesson did not take place due to student absence.',
      [lessonResultCode.STUDENT_CANCEL]: 'This lesson was cancelled by the student.',
      [lessonResultCode.TRAINER_SUBSTITUTION]: 'You have cancelled this lesson. It will be delivered by a substitute trainer.',
      [lessonResultCode.ADMIN_CANCEL]: 'This lesson was cancelled by an admin.',
      [lessonResultCode.STUDENT_EMERGENCY]: 'This lesson ended prematurely due to a student emergency.',
      [lessonResultCode.ADMIN_EMERGENCY]: 'This lesson ended prematurely due to an admin emergency.'
    },
    recordingUnavailable: 'Safari currently does not support this feature',
    vBackgroundUnavailable: 'Safari currently does not support this feature',
    chatUnavailable: 'Chat is temporarily unavailable',
    messageDisclaimer: 'You will be able to send messages when the lesson starts.',
    endNote: 'You will no longer be able to send messages if either you or the student leaves MyStage within the next 10 minutes after the lesson time.',
    lessonEndNote: 'Messaging is disabled as the lesson time has ended.',
    chatEvents: {
      11: {
        remote: '{0} has joined',
        local: 'You have joined'
      },
      12: {
        remote: '{0} has left',
        local: 'You have left'
      },
      21: {
        remote: '{0}\'s video & audio connection has been restored',
        local: 'Your video & audio connection has been restored'
      },
      22: {
        remote: '{0}\'s video & audio connection has been lost',
        local: 'Your video & audio connection has been lost'
      }
    },
    exitMessage: {
      unknown: 'There was a problem during this lesson ({re}). You will not be able re-enter the lesson room',
      [lessonResultCode.TRAINER_REQUESTS_SKYPE]: 'This lesson was terminated because <b>you reported a problem with MyStage</b>. You will not be able to re-enter the lesson room',
      [lessonResultCode.PENDING_EMERGENCY]: 'This lesson ended prematurely due to a <b>student emergency</b>. You will not be able to re-enter the lesson room',
      [lessonResultCode.STUDENT_NOSHOW]: 'This lesson did not take place due to a <b>student no-show</b>. You will not be able to re-enter the lesson room.',
      [lessonResultCode.STUDENT_ABSENT]: 'This lesson did not take place due to <b>student absence</b>. You will not be able to re-enter the lesson room.',
      [lessonResultCode.STUDENT_CANCEL]: 'This lesson was <b>cancelled by the student</b>. You will not be able to re-enter the lesson room.',
      [lessonResultCode.ADMIN_CANCEL]: 'This lesson was <b>cancelled by an admin</b>. You will not be able to re-enter the lesson room.',
      [lessonResultCode.STUDENT_EMERGENCY]: 'This lesson ended prematurely due to a <b>student emergency</b>. You will not be able to re-enter the lesson room.',
      [lessonResultCode.ADMIN_EMERGENCY]: 'This lesson ended prematurely due to an <b>admin emergency</b>. You will not be able to re-enter the lesson room.',
    },
    errorView: {
      generalMsg: '1. Close MyStage <br>2. Log out of the Bizmates portal <br>3. Log back in <br>4. Open MyStage again',
      generalTitle: 'An error has occured.',
      nbfMsg: 'Your system clock is not properly synchronized. <br>' +
              'To synchronize your system clock to the Microsoft Timer Servers, refer to Q16 on the MyStage FAQ:<br>' +
              '<a href="https://www.koseijyou.ph/portal/modules/admin/faqs/MyStageFAQs.php">' +
              'https://www.koseijyou.ph/portal/modules/admin/faqs/MyStageFAQs.php' +
              '</a>'
    },
    errors: {
      general: {
        message: '<a href="' + bizmatesSiteHomeUrl + '">Click here to go to the home screen.</a>'
      },
      [errorCodes.ME001]: {
        message: 'An error occurred when entering the lesson room.<br><br>' +
        'We apologize for the inconvenience, but the time set on your device may be behind the time in your region (time zone).' +
        'Please adjust the time settings on your device and re-enter the lesson room.'
      },
      [errorCodes.ME002]: {
        message: 'An error occurred when entering the lesson room.<br><br>' +
        'Please click the link below to log out of MyBizmates on the home screen and log in again to re-enter the lesson room.'
      },
      [errorCodes.ME003]: {
        message: 'An error occurred when entering the lesson room.<br><br>' +
        'Please click the link below to log out of MyBizmates on the home screen and log in again to re-enter the lesson room.'
      },
      [errorCodes.ME004]: {
        message: 'An error occurred when entering the lesson room.<br><br>' +
        'Please click the link below to log out of MyBizmates on the home screen and log in again to re-enter the lesson room.'
      },
    },
    browserNotSupported: {
      title: 'Sorry, your browser is not supported.',
      description: 'MyStage uses technology that is not supported in your current browser. <br/> Please upgrade or switch to another browser. We recommend using any of the following:',
      version: 'or later',
      close: 'Close'
    },
    deviceCheck: {
      title: 'Check your devices',
      subtitle: 'Please confirm your camera, mic, and speakers are properly set up <br/> and working before starting your lesson.',
      enter: 'Enter MyStage now',
      speakerTest: 'Test',
      deviceError: 'The device cannot be found.',
      selectDevice: 'Select Device',
      selectCamera: 'Select camera',
      selectMicrophone: 'Select microphone',
      selectSpeaker: 'Select speaker',
      connectingCamera: 'Initializing camera'
    },
    chatNotification: 'New message',
    networkAlert: 'Internet connection is unstable',
    networkAudioOnlyAlert: 'This video is paused due to unstable internet connection',
    recordDialog: {
      title: 'Note',
      message: 'To begin recording, please select and share the screen you wish to record.',
      checkLabel: 'Do not display this message again',
      buttons: {
        confirm: 'Confirm',
        cancel: 'Cancel'
      }
    },
    sideMenu: {
      title: 'Setting',
      deviceTitle: 'Sound and video setting',
      colorTitle: 'Color preference',
      otherTitle: 'Other',
      faqTitle: 'Frequently Asked Questions',
      virtualBgTitle: 'Video Background',
      color: {
        light: 'Light Grey',
        dark: 'Black'
      },
      virtualBg: {
        none: 'None',
        blur: 'Blur'
      }
    },
    htmlBuilder: {
      error: 'HTML or css content cannot be empty.',
      unauthorized: 'Unauthorized access.',
      create: 'HTML was successfully created.',
      update: 'HTML was successfully updated.',
      notAllowed: 'Sorry, you are not allowed to approve changes.',
      approved: 'Successfully approved the changes',
      disabled: 'This feature is currently disabled.'
    },
    trainerTag: {
      hasBeenTag: '“{0}” has been tagged',
      edited: 'Edited'
    },
    systemDefault: 'System Default',
    deviceNotification: {
      success: 'The device has been changed to {0}.',
      successAndroid: 'The device has been changed to {0}.\n\nSwitching from speaker to Bluetooth may cause issues on Android. Please refresh the page to resolve it.',
      warning: 'Something is wrong with your device. Please try again or use different device.',
      info: 'Your device list has been updated. Go to Menu to manage your devices.'
    },
    deviceNotificationBeforeLesson: 'It is not available to change or test the device on this screen before the lesson.',
    deviceSelectionDisabled: 'Manual device selection is not supported on mobile',
    screenRecordNotification: 'Student rejoined the lesson. Please reselect the screen to resume recording.',
    modal: {
      close: 'Close'
    },
    autoplayNotif: {
      title: 'Audio Could Not Be Played',
      content: 'Your browser blocked audio playback. <br/>Click the <b>"Play Audio"</b> button to continue.'
    },
    chatSupportText: "Chat Support",
    messageSendText: "Select a Message to send in Japanese",
    clickHereText: "Click Here",
  }
}
